import { useQuery } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";
import type { SignedInUser } from "@/lib/api-types";

/**
 * The one place every later screen asks "what's my role" (AD-14: "the
 * frontend mirrors route role declarations to decide what to render and
 * enforces nothing"). The source of truth is always `GET /auth/me` — never a
 * client-side guess, and never a value cached anywhere else that could go
 * stale relative to the server's own answer (VC-2.1-17). TanStack Query
 * caches the response so every component that calls this hook shares one
 * answer and one network request.
 */
export const CURRENT_ACTOR_QUERY_KEY = ["auth", "me"] as const;

export function useCurrentActor() {
  return useQuery({
    queryKey: CURRENT_ACTOR_QUERY_KEY,
    queryFn: () => apiFetch<SignedInUser>("/auth/me"),
  });
}

/**
 * Read-only rendering is by absence, not `disabled` (DESIGN.md § Do's and
 * Don'ts): a screen asks this before deciding whether to render a primary,
 * edit or destructive control at all, rather than rendering it disabled.
 * Returns `true` (withhold the control) while the actor is still loading or
 * unknown — the safer failure mode is a control that briefly doesn't render
 * and then appears once an Owner or Member is confirmed, not one that
 * flashes visible and then has to be yanked away from a Read-only user.
 */
export function useIsReadOnly(): boolean {
  const { data } = useCurrentActor();
  return data?.role !== "owner" && data?.role !== "member";
}

/**
 * Story 3.8: some routes are Owner-only, not merely "not Read-only" (the
 * Alerts screen's threshold edit — a change to system-wide configuration,
 * not a per-user preference, `app/api/alert_rules.py`'s own judgement call
 * 4). `useIsReadOnly` alone cannot express that distinction, since it
 * treats Member the same as Owner. A screen with an Owner-only affordance
 * checks this instead, and — the same absence-based rule, the same "safer
 * failure mode while unknown" default `useIsReadOnly` already established —
 * withholds the control until the actor is confirmed to actually be an
 * Owner, not merely not-yet-known-to-be-something-else.
 */
export function useIsOwner(): boolean {
  const { data } = useCurrentActor();
  return data?.role === "owner";
}
